import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import LoaderIcon from '@/images/icons/Loader';
import { useFetch } from '@/hooks/useFetch';
import { AuthState } from '@/types';
import { Input } from '@/components/Form/Input';
import EmailChangeModal from '@/components/EmailChangeModal';
import { fetchAPI } from '@/api';
import { Alert } from '@/components/Alert';

const Account = () => {
  const auth = useSelector((state: { auth: AuthState }) => state.auth);

  const [emailChangeModalOpen, setEmailChangeModalOpen] = useState<boolean>(false);
  const [weekly, setWeekly] = useState<boolean>(false);
  const [initialWeekly, setInitialWeekly] = useState<boolean>(false);
  const [initialEmail, setInitialEmail] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [changedEmail, setChangedEmail] = useState<string>('');
  const [emailConfirmCode, setEmailConfirmCode] = useState<string>('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [errorChangeEmail, setErrorChangeEmail] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(true);
  const [hasResendBeenClicked, setHasResendBeenClicked] = useState<boolean>(false);
  const [isEmailVerifyLoading, setIsEmailVerifyLoading] = useState<boolean>(false);
  const [emailChanged, setEmailChanged] = useState<boolean>(false);

  const [weeklyUpdateSuccess, setWeeklyUpdateSuccess] = useState<boolean>(false);
  const [weeklyUpdateError, setWeeklyUpdateError] = useState<string | null>(null);
  const [isWeeklyLoading, setIsWeeklyLoading] = useState<boolean>(false);

  const { data, callFetch, isLoading }: any = useFetch('/account');

  useEffect(() => {
    callFetch();
  }, []);

  useEffect(() => {
    if (data) {
      if (data.email) {
        setInitialEmail(data.email);
        setEmail(data.email);
      }
      if (typeof data.isNotSubscribedWeeklyReports !== 'undefined') {
        setWeekly(data.isNotSubscribedWeeklyReports);
        setInitialWeekly(data.isNotSubscribedWeeklyReports);
      }
    }
  }, [data]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const sendCodeEmail = async () => {
    try {
      setIsEmailVerifyLoading(true);
      setErrorChangeEmail(null);
      setErrorCode(null);
      const response = await fetchAPI('/email/change', auth?.user?.token, { email });
      const data = await response.json();
      if (response.ok) {
        if (data.requestId) {
          setRequestId(data.requestId);
          setErrorChangeEmail(null);
          setResendTimer(60);
          setCanResend(false);
        } else {
          setEmailChangeModalOpen(false);
          setRequestId(null);
          window.location.reload();
        }
      } else {
        setErrorChangeEmail(data.message);
      }
    } catch (error: any) {
      console.log('Error creating recovery request:', error);
      setErrorChangeEmail(error?.response?.message || 'An error occurred, please try again later.');
      setCanResend(true);
    } finally {
      setIsEmailVerifyLoading(false);
    }
  };

  const onEmailResend = () => {
    setHasResendBeenClicked(true);
    onEmailSend();
  };

  const onEmailSend = () => {
    if (canResend) {
      setResendTimer(60);
      setCanResend(false);
      sendCodeEmail();
    }
  };

  const onEmailVerify = async () => {
    if (isEmailVerifyLoading) return;
    try {
      setErrorCode(null);
      setIsEmailVerifyLoading(true);
      const response = await fetchAPI('/email/change-verify', auth?.user?.token, {
        requestId,
        verificationCode: emailConfirmCode,
      });
      const data = await response.json();
      if (response.ok) {
        setErrorCode(null);
        setEmailChangeModalOpen(false);
        setEmailChanged(true);
        setInitialEmail(email);
        setCanResend(true);
        setRequestId(null);
      } else {
        setErrorCode(data.message);
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setErrorCode(error?.response?.message || 'An error occurred, please try again later.');
    } finally {
      setIsEmailVerifyLoading(false);
    }
  };

  const onAccountChangeEmail = (e: React.FormEvent) => {
    e.preventDefault();

    if (email !== changedEmail) {
      setRequestId(null);
      setCanResend(true);
    }

    setChangedEmail(email);
    setEmailChangeModalOpen(true);
  };

  // Manual weekly update function
  const onToggleWeekly = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setWeeklyUpdateError(null);
    setWeeklyUpdateSuccess(false);
    setWeekly(newValue);
    setIsWeeklyLoading(true);

    if (newValue !== initialWeekly) {
      try {
        const response = await fetchAPI('/account/update', auth?.user?.token,
          { isNotSubscribedWeeklyReports: newValue }
        );
        const data = await response.json();
        if (response.ok) {
          setWeeklyUpdateSuccess(true);
          setInitialWeekly(newValue);
          setTimeout(() => setWeeklyUpdateSuccess(false), 3000);
        } else {
          throw new Error(data.message || 'Update failed');
        }
      } catch (err: any) {
        setWeeklyUpdateError(err.message || 'An error occurred');
        setWeekly(initialWeekly);
      } finally {
        setIsWeeklyLoading(false);
      }
    }
  };

  return (
    <StaticLayoutInner>
      <h1 className="h1 col-start-1 col-end-2 mb-10">Account</h1>
      {isLoading ? (
        <div className="flex items-center justify-center py-1 pointer-events-none mt-16 md:mt-[120px]">
          <LoaderIcon className="animate-rotation w-10 h-10 text-brand-green" />
        </div>
      ) : (
        <div className="relative lg:w-[70%] lg:max-w-3xl">
          <form className="border-b border-b-[#DBDBDB] pb-10 mb-8" onSubmit={onAccountChangeEmail}>
            <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-5">Email</div>
            <Input
              className="h-12 bg-white rounded-[8px] mb-6"
              inputClassName="border-white"
              type="email"
              name="user-email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailChanged(false);
              }}
            />
            <button
              type="submit"
              className={classNames(
                'btn-primary min-w-[100px] disabled:bg-[#EDECE8]',
                isLoading || email === initialEmail ? 'cursor-not-allowed' : ''
              )}
              title={isLoading || email === initialEmail ? 'Email is the same' : ''}
              disabled={isLoading || email === initialEmail}
            >
              Change
            </button>
          </form>

          <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-8">Communication preferences</div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              name="user-weekly"
              checked={weekly}
              disabled={isWeeklyLoading}
              onChange={onToggleWeekly}
            />
            <div className="form-toggle" />
            <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">
              Weekly reports
            </span>
          </label>
          <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-5">
            Receive a summary of your weekly activity and updates
          </div>
        </div>
      )}

      <Alert isShow={emailChanged} status="success" absolute>
        Email updated
      </Alert>

      <Alert isShow={weeklyUpdateSuccess} status="success" absolute>
        Account updated
      </Alert>

      <Alert isShow={!!weeklyUpdateError} status="error" absolute>
        {weeklyUpdateError}
      </Alert>

      <EmailChangeModal
        modalOpen={emailChangeModalOpen}
        setModalOpen={(isOpen) => setEmailChangeModalOpen(isOpen)}
        email={email}
        setEmail={setEmail}
        emailConfirmCode={emailConfirmCode}
        setEmailConfirmCode={setEmailConfirmCode}
        resendTimer={resendTimer}
        onResend={onEmailResend}
        onSend={onEmailSend}
        onVerify={onEmailVerify}
        hasResendBeenClicked={hasResendBeenClicked}
        isLoading={isEmailVerifyLoading || auth?.isLoggingIn}
        requestId={requestId}
        error={errorChangeEmail}
        errorCode={errorCode}
      />
    </StaticLayoutInner>
  );
};

export default Account;
